import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import DataTableBase from "../interfaccia/DataTableBase";
import stilePulsanti from "../interfaccia/stilePulsanti";

const BULK_JOB_STATUS_LABELS = {
  PENDING: "In coda",
  RUNNING: "In esecuzione",
  SUCCESS: "Completato",
  PARTIAL_SUCCESS: "Completato con errori",
  ERROR: "Errore",
};

const BULK_JOB_TYPE_LABELS = {
  add_categories: "Aggiungi categorie",
  change_default_category: "Cambia categoria predefinita",
  always_on_add: "Aggiungi ad Always On",
  always_on_pool: "Rendi l'intero pool Always On",
  set_ecommerce_flags: "Attiva/disattiva su PS o AMZ",
  force_prestashop_sync: "Forza sync con P.S.",
  traduci_testi: "Traduzione testi",
  verifica_traduzioni_online: "Verifica traduzioni online",
};

const BULK_JOB_DOMAIN_LABELS = {
  web_categories: "Categorie web",
  always_on: "Always On",
  ecommerce_flags: "Attivazione e-commerce",
  prestashop_sync: "Sync con P.S.",
  traduzioni: "Traduzioni",
  translations_online: "Verifica traduzioni online",
};

const TERMINAL_JOB_STATUSES = new Set(["SUCCESS", "PARTIAL_SUCCESS", "ERROR"]);

// Retry (T16): solo job di traduzione terminati con errori o articoli non
// elaborati (es. arresto per quota esaurita).
export function canRetryJob(job, permessi) {
  return job?.jobType === "traduci_testi" && TERMINAL_JOB_STATUSES.has(job.status) &&
    (Number(job.errorCount ?? 0) > 0 || Number(job.processedCount ?? 0) < Number(job.totalItems ?? 0)) &&
    Boolean(permessi?.["usage:googleapis"]);
}

const ACTIVE_JOB_STATUSES = new Set(["PENDING", "RUNNING"]);
const JOB_POLL_INTERVAL_MS = 5000;

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString("it-IT");
};

const buildStatusBadgeClass = (status) => {
  if (status === "SUCCESS") {
    return "dark-green";
  }
  if (status === "PARTIAL_SUCCESS") {
    return "dark-orange";
  }
  if (status === "ERROR") {
    return "dark-red";
  }
  return "dark-blue";
};

function ModaBulkJobsPage({ token, utente, temaColori }) {
  const { buttonClassesWhite, buttonClassesGreen, inputClassesNoMeasure } =
    stilePulsanti;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [retrying, setRetrying] = useState("");

  const permissionsLoaded = Boolean(utente?.permessiAPI);
  const canView = permissionsLoaded
    ? Boolean(utente?.permessiAPI?.["update:database"])
    : true;

  const loadJobs = useCallback(
    async (mode = "load") => {
      if (!token || !canView) {
        return;
      }

      if (mode === "refresh") {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const response = await axios.get(`${apiUrl}/moda/articoli/bulk-jobs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            limit,
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(domainFilter ? { domain: domainFilter } : {}),
          },
        });

        const nextJobs = Array.isArray(response?.data?.jobs)
          ? response.data.jobs
          : [];
        setJobs(nextJobs);
      } catch (loadError) {
        console.error("Errore caricamento job massivi pubblicazione:", loadError);
        setError(
          loadError?.response?.data?.error ??
            loadError?.message ??
            "Impossibile recuperare lo storico job."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token, canView, limit, statusFilter, domainFilter]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const retryJob = useCallback(async (jobId) => {
    setRetrying(jobId);
    setError("");
    try {
      await axios.post(`${apiUrl}/moda/articoli/bulk-jobs/${encodeURIComponent(jobId)}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadJobs("refresh");
    } catch (retryError) {
      setError(retryError?.response?.data?.error ?? retryError?.message ?? "Retry del job non riuscito.");
    } finally {
      setRetrying("");
    }
  }, [token, loadJobs]);

  const filteredJobs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch.length) {
      return jobs;
    }

    return jobs.filter((job) => {
      const haystack = [
        job.jobId,
        job.requestedByDisplay,
        job.requestedBy,
        job.message,
        job.lastStep,
        BULK_JOB_STATUS_LABELS[job.status] || job.status,
        BULK_JOB_TYPE_LABELS[job.jobType] || job.jobType,
        BULK_JOB_DOMAIN_LABELS[job.domain] || job.domain,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [jobs, search]);

  const hasActiveJobs = useMemo(
    () => jobs.some((job) => ACTIVE_JOB_STATUSES.has(job.status)),
    [jobs]
  );

  useEffect(() => {
    if (!token || !canView || !hasActiveJobs) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      loadJobs("refresh");
    }, JOB_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [token, canView, hasActiveJobs, loadJobs]);

  const stats = useMemo(() => {
    return filteredJobs.reduce(
      (acc, job) => {
        acc.total += 1;
        if (ACTIVE_JOB_STATUSES.has(job.status)) {
          acc.active += 1;
        }
        if (job.status === "SUCCESS") {
          acc.success += 1;
        }
        if (job.status === "PARTIAL_SUCCESS" || job.status === "ERROR") {
          acc.withErrors += 1;
        }
        acc.updated += Number(job.updatedCount ?? 0);
        return acc;
      },
      {
        total: 0,
        active: 0,
        success: 0,
        withErrors: 0,
        updated: 0,
      }
    );
  }, [filteredJobs]);

  const columns = useMemo(
    () => [
      {
        name: "Job",
        sortable: true,
        minWidth: "250px",
        selector: (row) => row.createdAt || "",
        cell: (row) => (
          <div className="pv2">
            <div className="b">
              {BULK_JOB_TYPE_LABELS[row.jobType] || row.jobType}
            </div>
            <div className="f7 gray">
              <code>{row.jobId}</code>
            </div>
          </div>
        ),
      },
      {
        name: "Stato",
        sortable: true,
        width: "180px",
        selector: (row) => row.status || "",
        cell: (row) => (
          <span className={`b ${buildStatusBadgeClass(row.status)}`}>
            {BULK_JOB_STATUS_LABELS[row.status] || row.status}
          </span>
        ),
      },
      {
        name: "Dominio",
        sortable: true,
        width: "140px",
        selector: (row) => row.domain || "",
        cell: (row) => BULK_JOB_DOMAIN_LABELS[row.domain] || row.domain,
      },
      {
        name: "Richiesto da",
        sortable: true,
        minWidth: "180px",
        selector: (row) => row.requestedByDisplay || row.requestedBy || "",
        cell: (row) => row.requestedByDisplay || row.requestedBy || "",
      },
      {
        name: "Creato",
        sortable: true,
        width: "170px",
        selector: (row) => row.createdAt || "",
        cell: (row) => formatDateTime(row.createdAt),
      },
      {
        name: "Processati",
        sortable: true,
        width: "120px",
        selector: (row) => Number(row.processedCount ?? 0),
        right: true,
        cell: (row) => `${row.processedCount}/${row.totalItems}`,
      },
      {
        name: "Agg.",
        sortable: true,
        width: "90px",
        selector: (row) => Number(row.updatedCount ?? 0),
        right: true,
      },
      {
        name: "Dash.",
        sortable: true,
        width: "90px",
        selector: (row) => Number(row.dashboardCount ?? 0),
        right: true,
      },
      {
        name: "Err.",
        sortable: true,
        width: "80px",
        selector: (row) => Number(row.errorCount ?? 0),
        right: true,
        cell: (row) => (
          <span className={row.errorCount > 0 ? "dark-red b" : ""}>
            {row.errorCount}
          </span>
        ),
      },
    ],
    []
  );

  const ExpandableRow = ({ data }) => (
    <div className="pa3 bg-near-white">
      <div className="mb2">
        <strong>Ultimo step:</strong> {data.lastStep || "N/D"}
      </div>
      <div className="mb2">
        <strong>Messaggio:</strong> {data.message || "N/D"}
      </div>
      <div className="mb2">
        <strong>Avvio:</strong> {formatDateTime(data.startedAt) || "N/D"}
        {" | "}
        <strong>Fine:</strong> {formatDateTime(data.finishedAt) || "N/D"}
        {" | "}
        <strong>Heartbeat:</strong> {formatDateTime(data.heartbeatAt) || "N/D"}
      </div>
      {canRetryJob(data, utente?.permessiAPI) && (
        <div className="mb2">
          <button
            className={buttonClassesWhite}
            onClick={() => retryJob(data.jobId)}
            disabled={Boolean(retrying)}
          >
            {retrying === data.jobId ? "Riaccodo..." : "Riprova articoli in errore o non elaborati"}
          </button>
        </div>
      )}
      {Array.isArray(data.recentErrors) && data.recentErrors.length > 0 && (
        <div>
          <strong>Errori recenti:</strong>
          <ul className="mt2 mb0 pl3">
            {data.recentErrors.map((item, index) => (
              <li key={`${data.jobId}-${item.codVS}-${index}`} className="mb1">
                <code>{item.codVS}</code>: {item.message || "Errore non specificato"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (!canView) {
    return (
      <div className={`${temaColori} pa2 pa3-ns contentSubHeader`}>
        <div className="sfondoBiancoTema br3 pa4 shadow-4">
          Non hai i permessi per visualizzare questa pagina.
        </div>
      </div>
    );
  }

  return (
    <div className={`${temaColori} pa2 pa3-ns contentSubHeader`}>
      <div className="sfondoBiancoTema br3 pa3 shadow-4">
        <div className="flex flex-wrap justify-between items-center mb3">
          <div>
            <h3 className="ma0">Job massivi pubblicazione</h3>
            <p className="mt2 mb0 gray">
              Storico dei job asincroni lanciati dalla modale Funzioni in
              pubblicazione.
            </p>
          </div>
          <div className="mt3 mt0-l">
            <button
              className={buttonClassesWhite}
              onClick={() => loadJobs("refresh")}
              disabled={loading || refreshing}
            >
              {refreshing ? "Aggiorno..." : "Aggiorna"}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap mb3">
          <div className="w-100 w-25-l pa2">
            <div className="ba bordoColAlt br3 pa3">
              <div className="f6 gray">Job visualizzati</div>
              <div className="f3 b">{stats.total}</div>
            </div>
          </div>
          <div className="w-100 w-25-l pa2">
            <div className="ba bordoColAlt br3 pa3">
              <div className="f6 gray">Attivi</div>
              <div className="f3 b dark-blue">{stats.active}</div>
            </div>
          </div>
          <div className="w-100 w-25-l pa2">
            <div className="ba bordoColAlt br3 pa3">
              <div className="f6 gray">Completati OK</div>
              <div className="f3 b dark-green">{stats.success}</div>
            </div>
          </div>
          <div className="w-100 w-25-l pa2">
            <div className="ba bordoColAlt br3 pa3">
              <div className="f6 gray">Con errori</div>
              <div className="f3 b dark-red">{stats.withErrors}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end mb3">
          <div className="w-100 w-third-l pr2-l mb2">
            <label className="db f7 mb1">Cerca</label>
            <input
              className={inputClassesNoMeasure}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="jobId, utente, tipo, messaggio..."
            />
          </div>
          <div className="w-100 w-20-l pr2-l mb2">
            <label className="db f7 mb1">Stato</label>
            <select
              className={inputClassesNoMeasure}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="PENDING">In coda</option>
              <option value="RUNNING">In esecuzione</option>
              <option value="SUCCESS">Completati</option>
              <option value="PARTIAL_SUCCESS">Completati con errori</option>
              <option value="ERROR">Errori</option>
            </select>
          </div>
          <div className="w-100 w-20-l pr2-l mb2">
            <label className="db f7 mb1">Dominio</label>
            <select
              className={inputClassesNoMeasure}
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="web_categories">Categorie web</option>
              <option value="always_on">Always On</option>
              <option value="ecommerce_flags">Attivazione e-commerce</option>
              <option value="prestashop_sync">Sync con P.S.</option>
              <option value="traduzioni">Traduzioni</option>
              <option value="translations_online">Verifica traduzioni online</option>
            </select>
          </div>
          <div className="w-100 w-20-l pr2-l mb2">
            <label className="db f7 mb1">Limite</label>
            <select
              className={inputClassesNoMeasure}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
          <div className="w-100 w-auto-l mb2">
            <button
              className={buttonClassesGreen}
              onClick={() => loadJobs("refresh")}
              disabled={loading || refreshing}
            >
              Applica / aggiorna
            </button>
          </div>
        </div>

        {hasActiveJobs && (
          <div className="mb3 pa2 br3 bg-washed-blue dark-blue">
            Polling automatico attivo: la pagina si aggiorna da sola finché ci
            sono job in coda o in esecuzione.
          </div>
        )}

        {error && (
          <div className="mb3 pa2 br3 bg-washed-red dark-red">{error}</div>
        )}

        <DataTableBase
          columns={columns}
          data={filteredJobs}
          progressPending={loading}
          paginationPerPage={10}
          noDataComponent={
            <div className="pa4">
              Nessun job trovato con i filtri correnti.
            </div>
          }
          expandableRows
          expandableRowsComponent={ExpandableRow}
        />
      </div>
    </div>
  );
}

export default ModaBulkJobsPage;
